// src/components/common/Toast.js
import React, { useEffect, useRef } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
} from '@mui/material';

const Toast = ({
  open,
  onClose,
  message,
  severity = 'error',
  title,
  autoHideDuration = 6000,
  position = { vertical: 'bottom', horizontal: 'right' },
  variant = 'filled', // 'filled', 'outlined', 'standard'
}) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // MUI pauses its own timer on window blur. The refresh dialog keeps focus,
  // so that timer is cleared and the toast stays until the X is clicked.
  useEffect(() => {
    if (!open || !autoHideDuration) return undefined;
    const id = window.setTimeout(() => {
      onCloseRef.current?.({}, 'timeout');
    }, autoHideDuration);
    return () => window.clearTimeout(id);
  }, [open, autoHideDuration, title, message]);

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      disableWindowBlurListener
      onClose={onClose}
      anchorOrigin={position}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
    >
      <Alert 
        onClose={onClose} 
        severity={severity} 
        variant={variant}
        sx={{ 
          width: '100%',
          minWidth: '300px',
          maxWidth: '500px',
          boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
          '& .MuiAlert-icon': {
            fontSize: '1.2rem',
          },
          '& .MuiAlert-message': {
            fontSize: '0.875rem',
          },
          // Custom colors based on severity
          ...(severity === 'success' && {
            backgroundColor: '#4caf50',
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff',
            },
          }),
          ...(severity === 'error' && {
            backgroundColor: '#f44336',
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff',
            },
          }),
          ...(severity === 'warning' && {
            backgroundColor: '#ff9800',
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff',
            },
          }),
          ...(severity === 'info' && {
            backgroundColor: '#2196f3',
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff',
            },
          }),
        }}
      >
        {title && <AlertTitle sx={{ fontSize: '0.9rem', fontWeight: 600 }}>{title}</AlertTitle>}
        {message}
      </Alert>
    </Snackbar>
  );
};

export default Toast;